/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – WaterQualityPanel.tsx FINAL
// Indice calitate apă (WQI) per fermă + trend + conformitate UE
// ============================================================

import React, { useState } from 'react';
import { Modal, Tag, Tooltip, Progress } from 'antd';
import {
  ApartmentOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  CloseCircleOutlined, RiseOutlined, FallOutlined, MinusOutlined,
  InfoCircleOutlined, EnvironmentOutlined, FireOutlined,
} from '@ant-design/icons';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip as RTooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell,
} from 'recharts';

import { mockWQI, mockFarms } from '../../data/mockData';
import { WaterQualityIndex, WQILevel } from '../../types';

// ─── Constante WQI ────────────────────────────────────────

const WQI_COLOR: Record<WQILevel, string> = {
  excellent: '#10b981',
  good:      '#6366f1',
  moderate:  '#f59e0b',
  poor:      '#f97316',
  critical:  '#ef4444',
};
const WQI_BG: Record<WQILevel, string> = {
  excellent: '#d1fae5',
  good:      '#eef2ff',
  moderate:  '#fef3c7',
  poor:      '#fff7ed',
  critical:  '#fff5f5',
};
const WQI_LABEL: Record<WQILevel, string> = {
  excellent: 'Excelent',
  good:      'Bun',
  moderate:  'Moderat',
  poor:      'Slab',
  critical:  'Critic',
};
const WQI_ICON: Record<WQILevel, React.ReactNode> = {
  excellent: <CheckCircleOutlined />,
  good:      <CheckCircleOutlined />,
  moderate:  <ExclamationCircleOutlined />,
  poor:      <ExclamationCircleOutlined />,
  critical:  <CloseCircleOutlined />,
};
const TREND_ICON = {
  improving: <RiseOutlined style={{ color: '#10b981' }} />,
  stable:    <MinusOutlined style={{ color: '#94a3b8' }} />,
  degrading: <FallOutlined  style={{ color: '#ef4444' }} />,
};
const TREND_LABEL = {
  improving: 'Îmbunătățire',
  stable:    'Stabil',
  degrading: 'Deteriorare',
};
const COMPLIANCE_COLOR = {
  compliant:     '#10b981',
  warning:       '#f59e0b',
  non_compliant: '#ef4444',
};
const COMPLIANCE_LABEL = {
  compliant:     'Conform',
  warning:       'Atenție',
  non_compliant: 'Neconform',
};

// ─── Gauge vizual WQI ─────────────────────────────────────

const WQIGauge: React.FC<{ score: number; level: WQILevel }> = ({ score, level }) => {
  const color = WQI_COLOR[level];
  const angle = (score / 100) * 180 - 90; // -90 to 90 degrees

  return (
    <div style={{ position: 'relative', width: 120, height: 70, margin: '0 auto' }}>
      <svg viewBox="0 0 120 70" style={{ width: '100%', height: '100%' }}>
        {/* Background arc */}
        <path d="M 10 65 A 55 55 0 0 1 110 65" fill="none" stroke="#f1f5f9" strokeWidth="10" strokeLinecap="round" />
        {/* Score arc */}
        <path
          d={`M 10 65 A 55 55 0 0 1 ${60 + 50 * Math.cos(((score / 100) * 180 - 180) * Math.PI / 180)} ${65 + 50 * Math.sin(((score / 100) * 180 - 180) * Math.PI / 180)}`}
          fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
        />
        {/* Score text */}
        <text x="60" y="58" textAnchor="middle" fontSize="20" fontWeight="800" fill={color}>{score}</text>
        <text x="60" y="68" textAnchor="middle" fontSize="8" fill="#94a3b8">/ 100</text>
      </svg>
    </div>
  );
};

// ─── Card fermă ───────────────────────────────────────────

const FarmWQICard: React.FC<{ wqi: WaterQualityIndex; onClick: () => void }> = ({ wqi, onClick }) => {
  const color  = WQI_COLOR[wqi.level];
  const farm   = mockFarms.find(f => f.id === wqi.farmId);
  const sCount = farm?.sensorIds.length ?? 0;

  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff', borderRadius: 14,
        border: `1px solid ${wqi.level === 'critical' ? '#fecaca' : wqi.level === 'poor' ? '#fed7aa' : '#e2e8f0'}`,
        padding: '18px 20px', cursor: 'pointer',
        boxShadow: wqi.level === 'critical'
          ? '0 4px 16px rgba(239,68,68,0.12)'
          : '0 1px 3px rgba(0,0,0,0.05)',
        transition: 'all 0.2s',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.10)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLDivElement).style.boxShadow = wqi.level === 'critical' ? '0 4px 16px rgba(239,68,68,0.12)' : '0 1px 3px rgba(0,0,0,0.05)'; }}
    >
      {/* Header fermă */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {wqi.farmName}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
            <EnvironmentOutlined style={{ fontSize: 10 }} />
            {farm?.location.label}
          </div>
        </div>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
          background: WQI_BG[wqi.level], color,
          display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, marginLeft: 8,
        }}>
          {WQI_ICON[wqi.level]} {WQI_LABEL[wqi.level]}
        </span>
      </div>

      {/* Gauge */}
      <WQIGauge score={wqi.score} level={wqi.level} />

      {/* Stats footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#64748b' }}>
          {TREND_ICON[wqi.trend]}
          <span>{TREND_LABEL[wqi.trend]}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99,
            background: `${COMPLIANCE_COLOR[wqi.complianceStatus]}15`,
            color: COMPLIANCE_COLOR[wqi.complianceStatus],
          }}>
            {COMPLIANCE_LABEL[wqi.complianceStatus]}
          </span>
          <span style={{ fontSize: 10, color: '#94a3b8' }}>{sCount} senzori</span>
        </div>
      </div>
    </div>
  );
};

// ─── Modal detalii fermă ──────────────────────────────────

const WQIDetailModal: React.FC<{ wqi: WaterQualityIndex | null; onClose: () => void }> = ({ wqi, onClose }) => {
  if (!wqi) return null;
  const color = WQI_COLOR[wqi.level];

  const radarData = wqi.parameters.map(p => ({
    param: p.name.length > 12 ? p.name.slice(0, 12) + '…' : p.name,
    score: p.score,
    pct:   Math.round((p.value / p.maxAllowed) * 100),
  }));

  return (
    <Modal
      open={!!wqi} onCancel={onClose} footer={null}
      width={680} title={null}
      styles={{ body: { padding: 0 } }}
      style={{ borderRadius: 18 }}
    >
      {/* Header */}
      <div style={{ padding: '24px 28px', background: `${color}08`, borderBottom: '1px solid #f1f5f9', borderRadius: '18px 18px 0 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: WQI_BG[wqi.level], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, color }}>
            <ApartmentOutlined />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{wqi.farmName}</h3>
            <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: WQI_BG[wqi.level], color }}>
                {WQI_ICON[wqi.level]} {WQI_LABEL[wqi.level]} — Scor {wqi.score}/100
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 99, background: `${COMPLIANCE_COLOR[wqi.complianceStatus]}15`, color: COMPLIANCE_COLOR[wqi.complianceStatus] }}>
                {COMPLIANCE_LABEL[wqi.complianceStatus]}
              </span>
              <span style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                {TREND_ICON[wqi.trend]} {TREND_LABEL[wqi.trend]}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Radar chart */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Profil parametri monitorizați</div>
          <ResponsiveContainer width="100%" height={200}>
            <RadarChart data={radarData} margin={{ top: 8, right: 32, bottom: 8, left: 32 }}>
              <PolarGrid stroke="#f1f5f9" />
              <PolarAngleAxis dataKey="param" tick={{ fontSize: 10, fill: '#64748b' }} />
              <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9, fill: '#94a3b8' }} />
              <Radar name="Scor calitate" dataKey="score" stroke={color} fill={color} fillOpacity={0.2} />
              <RTooltip contentStyle={{ borderRadius: 10, fontSize: 11 }} formatter={(v: any) => [`${v}/100`, 'Scor']} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Parametri detaliu */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Detaliu parametri</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {wqi.parameters.map((p, i) => {
              const pct     = Math.round((p.value / p.maxAllowed) * 100);
              const exceeds = p.value >= p.maxAllowed;
              const overLod = p.value >= p.lod;
              return (
                <div key={i} style={{ padding: '12px 14px', borderRadius: 10, border: `1px solid ${exceeds ? '#fecaca' : overLod ? '#fde68a' : '#f1f5f9'}`, background: exceeds ? '#fff5f5' : overLod ? '#fffbeb' : '#fafafa' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{p.name}</span>
                      <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 8 }}>{p.euDirective}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: exceeds ? '#ef4444' : overLod ? '#f59e0b' : '#10b981' }}>
                        {p.value} {p.unit}
                      </span>
                      <span style={{ fontSize: 10, color: '#94a3b8' }}>/ {p.maxAllowed} {p.unit} MAX</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, height: 6, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 99, width: `${Math.min(pct, 100)}%`, background: exceeds ? '#ef4444' : overLod ? '#f59e0b' : '#10b981', transition: 'width 0.6s ease' }} />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: exceeds ? '#ef4444' : '#64748b', minWidth: 36 }}>{pct}%</span>
                    <span style={{ fontSize: 10, color: '#94a3b8' }}>Scor: {p.score}/100</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
          Ultima actualizare: {new Date(wqi.lastUpdated).toLocaleString('ro-RO')} · Greutăți conform Directivelor UE cadru apă 2000/60/CE
        </div>
      </div>
    </Modal>
  );
};

// ─── Panel principal ──────────────────────────────────────

const WaterQualityPanel: React.FC = () => {
  const [selected, setSelected] = useState<WaterQualityIndex | null>(null);

  const avgScore  = Math.round(mockWQI.reduce((s, w) => s + w.score, 0) / mockWQI.length);
  const compliant = mockWQI.filter(w => w.complianceStatus === 'compliant').length;
  const warning   = mockWQI.filter(w => w.complianceStatus === 'warning').length;
  const nonComp   = mockWQI.filter(w => w.complianceStatus === 'non_compliant').length;
  const degrading = mockWQI.filter(w => w.trend === 'degrading').length;

  const barData = mockWQI.map(w => ({
    name:  w.farmName.split('–')[0].trim().replace('Ferma ', ''),
    score: w.score,
    color: WQI_COLOR[w.level],
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header */}
      <div>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>
          Calitate Apă
        </h2>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>
          Indice WQI per fermă · Conformitate directive UE · {mockWQI.length} zone monitorizate
        </span>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {[
          { label: 'Scor mediu WQI',   value: `${avgScore}/100`, color: avgScore >= 70 ? '#10b981' : avgScore >= 50 ? '#f59e0b' : '#ef4444', bg: avgScore >= 70 ? '#d1fae5' : avgScore >= 50 ? '#fef3c7' : '#fee2e2', icon: <ApartmentOutlined /> },
          { label: 'Zone conforme',     value: compliant,         color: '#10b981', bg: '#d1fae5',  icon: <CheckCircleOutlined /> },
          { label: 'Zone atenție',      value: warning,           color: '#f59e0b', bg: '#fef3c7',  icon: <ExclamationCircleOutlined /> },
          { label: 'Zone neconforme',   value: nonComp,           color: '#ef4444', bg: '#fee2e2',  icon: <CloseCircleOutlined /> },
        ].map((k, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: k.color }}>
                {k.icon}
              </div>
              <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{k.label}</span>
            </div>
            <div style={{ fontSize: 26, fontWeight: 900, color: k.color, letterSpacing: '-0.5px' }}>{k.value}</div>
            {i === 3 && degrading > 0 && (
              <div style={{ fontSize: 10, color: '#f97316', marginTop: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                <FallOutlined /> {degrading} cu trend descendent
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Bar chart comparativ */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Comparativ scor WQI per fermă</div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 14 }}>Linie roșie = prag minim acceptabil (50). Click pe fermă pentru detalii.</div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={barData} margin={{ top: 4, right: 16, bottom: 0, left: -16 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
            <RTooltip contentStyle={{ borderRadius: 10, fontSize: 11, border: '1px solid #e2e8f0' }} formatter={(v: any) => [`${v}/100`, 'Scor WQI']} />
            <Bar dataKey="score" radius={[6, 6, 0, 0]} onClick={(_, idx) => setSelected(mockWQI[idx])}>
              {barData.map((b, i) => <Cell key={i} fill={b.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        {/* Reference line 50 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
          <div style={{ flex: 1, height: 1, borderTop: '2px dashed #ef4444', opacity: 0.4 }} />
          <span style={{ fontSize: 10, color: '#ef4444', fontWeight: 600 }}>Prag minim 50</span>
        </div>
      </div>

      {/* Grid ferme */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Detaliu per fermă — click pentru analiză completă</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {mockWQI.map(wqi => (
            <FarmWQICard key={wqi.farmId} wqi={wqi} onClick={() => setSelected(wqi)} />
          ))}
        </div>
      </div>

      {/* Legend */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: '14px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Scală WQI — Interpretare scoruri</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {(Object.keys(WQI_LABEL) as WQILevel[]).map(lvl => (
            <div key={lvl} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 7, background: WQI_BG[lvl], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: WQI_COLOR[lvl] }}>
                {WQI_ICON[lvl]}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: WQI_COLOR[lvl] }}>{WQI_LABEL[lvl]}</div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>
                  {lvl === 'excellent' ? '85–100' : lvl === 'good' ? '70–84' : lvl === 'moderate' ? '50–69' : lvl === 'poor' ? '25–49' : '0–24'}
                </div>
              </div>
            </div>
          ))}
          <div style={{ marginLeft: 'auto', fontSize: 10, color: '#94a3b8', alignSelf: 'center', maxWidth: 280, textAlign: 'right' }}>
            WQI calculat conform Dir. 2000/60/CE Cadrul Apei · Directive specifice antibiotice, pesticide, hormoni și nitrați
          </div>
        </div>
      </div>

      <WQIDetailModal wqi={selected} onClose={() => setSelected(null)} />
    </div>
  );
};

export default WaterQualityPanel;